import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';

import { PATH } from '../../../constants/path';
import { PAYPAL } from '../../../constants/paymentMethods';
import { MODAL_TYPE_KYC } from '../../../services/modal/constants';
import { KYC_STATE_VALIDATED } from '../../../services/kyc/constants';
import { PENDING, VALIDATED, WAITING_TRANSACTION } from '../../../services/checkout/constants';

import { showModal } from '../../../services/modal/actions';

import {
    createOrder,
    createPaypalOrder,
    capturePaypalOrder,
    getPaypalOrderState
} from '../../../services/paypal/utils';

import { LoadingComponent } from '../../loading/LoadingComponent';

const PaypalButton = ({
    userInfo,
    partnerId,
    kycNeeded,
    i18n,
    token,
    ebon,
    ebonPaymentConfig,
    paymentMethods,
    paypalCost,
    kycState,
    showModal
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [order, setOrder] = useState(null);
    const [{ isPending }] = usePayPalScriptReducer();
    const [paypalOrderId, setPaypalOrderId] = useState(false);

    const disabled = isPending || loading || kycNeeded;

    const paypalAllowed = ebonPaymentConfig.paypalAllowed && paymentMethods.includes(PAYPAL);
    const hasFreeFacial = ebon.ebons.freeFacialValue !== undefined;

    useEffect(() => {
        if (kycState === KYC_STATE_VALIDATED) {
            const successUrl = `${PATH.EBON_CONFIRMATION_PATH}-${partnerId}?orderId=${order.id}&paypal=${paypalCost}&ref=${paypalOrderId}`;
            window.location.href = successUrl;
        }
    }, [kycState]);

    const getOrderState = async (interval, orderId) => {
        try {
            const { payload } = await getPaypalOrderState(orderId);
            const { ebonOrderState } = payload;

            if (ebonOrderState !== PENDING) {
                if (ebonOrderState === WAITING_TRANSACTION) {
                    showModal(MODAL_TYPE_KYC, {
                        hideCloseButton: false,
                        useCase: null
                    });
                } else {
                    console.error('Failed to capture PayPal order :', orderId);
                    setLoading(false);
                    setError(i18n.PRECONDITION_FAILED_ERROR_MESSAGE);
                }
                clearInterval(interval);
            }
        } catch (error) {
            console.error('Error while checking order state:', error);
        }
    };

    /* Step 1 */
    const onCreateOrder = async () => {
        setLoading(true);
        try {
            const order = await createOrder(token, partnerId, userInfo.phoneNumber, !hasFreeFacial);
            if (order && order.id) {
                setOrder(order);

                const widgetLink = await createPaypalOrder(token, userInfo, partnerId, order.id);
                if (widgetLink) {
                    return widgetLink;
                }
            }

            setLoading(false);
            setError(i18n.DEFAULT_ERROR_MESSAGE);
        } catch (e) {
            setLoading(false);
            setError(e.message);
            throw e;
        }
    };

    /* Step 2 */
    const onApprove = async data => {
        const errorUrl = `${PATH.EBON_ERROR_PATH}-${partnerId}?paiement=error&orderId=${order.id}`;
        try {
            const pspOrderId = data.orderID;
            setPaypalOrderId(data.orderID);
            const response = await capturePaypalOrder(token, pspOrderId, userInfo, partnerId);
            if (
                response &&
                response.status === 200 &&
                response.ebonOrderUuid &&
                response.ebonOrderState === VALIDATED
            ) {
                const successUrl = `${PATH.EBON_CONFIRMATION_PATH}?orderId=${response.ebonOrderUuid}&paypal=${paypalCost}&ref=${pspOrderId}`;
                window.location.href = successUrl;
            } else if (response && response.status === 412) {
                setLoading(true);
                const interval = setInterval(() => getOrderState(interval, pspOrderId), 100);
            } else {
                console.error('Failed to capture PayPal order :', pspOrderId);
                window.location.href = errorUrl;
            }
        } catch (e) {
            window.location.href = errorUrl;
        }
    };

    const onError = err => {
        setLoading(false);
        console.error('PayPal error : ', err);
        setError(i18n.DEFAULT_ERROR_MESSAGE);
    };

    const onCancel = () => {
        setLoading(false);
        setError(null);
    };

    const onClick = () => {
        if (kycNeeded) {
            showModal(MODAL_TYPE_KYC, {
                hideCloseButton: false,
                useCase: null
            });
        }
    };

    return (
        <div style={{ marginTop: '10px' }}>
            {paypalAllowed && (
                <PayPalButtons
                    key={kycNeeded ? 'kyc-needed' : 'kyc-done'}
                    style={{
                        shape: 'rect',
                        layout: 'vertical'
                    }}
                    createOrder={kycNeeded ? undefined : onCreateOrder}
                    onApprove={onApprove}
                    onError={onError}
                    onCancel={onCancel}
                    disabled={disabled}
                    onClick={onClick}
                />
            )}

            {isPending && <LoadingComponent />}

            {error && <p className="error">{error}</p>}
        </div>
    );
};

PaypalButton.propTypes = {
    userInfo: PropTypes.object,
    partnerId: PropTypes.number,
    kycNeeded: PropTypes.bool
};

const mapStateToProps = state => {
    return {
        i18n: state.i18n.page.EBON.PAYPAL,
        token: state.authentication.token,
        ebon: state.ebon,
        ebonPaymentConfig: state.ebon.ebons.ebonPaymentConfig,
        paymentMethods: state.ebon.lock[0].paymentMethods || [],
        paypalCost: state.ebon.ebons.ebonPaymentConfig.paypalCost,
        kycState: state.kyc.state
    };
};

const mapDispatchToProps = {
    showModal
};

export default connect(mapStateToProps, mapDispatchToProps)(PaypalButton);



// WEBPACK FOOTER //
// ./src/components/funnel/buttons/PaypalButton.js